import React, { useState } from "react";
import { IDoctor, ITreatment } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";
import { DoctorFormModal } from "./DoctorFormModal"; // Import the new modal
import bookUrl from "../../../../api/bookUrl";
import { toast } from "react-toastify";
interface Props {
  doctors: IDoctor[];
  treatments: ITreatment[]; // Needed for the multi-select dropdown
  onDoctorsChange: (doctors: IDoctor[]) => void;
}

// Single Doctor Card
const DoctorCard: React.FC<{
  doctor: IDoctor;
  allTreatments: ITreatment[];
  onDelete: () => void;
  onEdit: () => void; // Changed from (id: string)
}> = ({ doctor, allTreatments, onDelete, onEdit }) => {
  const getAssignedTreatments = () => {
    return doctor?.assignedTreatmentIds
      ?.map((id) => allTreatments.find((t) => t.id === id)?.name)
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg shadow-slate-200/50 flex flex-col">
      <img
        src={doctor.image}
        alt={doctor.firstName}
        className="w-full h-[150px] object-cover rounded-t-lg"
      />
      <div className="p-3 flex-grow">
        <h4 className="text-sm font-semibold text-slate-800 mb-1.5">
          Dr. {doctor.firstName + " " + doctor.lastName}
        </h4>
        <div className="font-semibold text-xs text-primary mb-2">
          {doctor.speciality}
        </div>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">Studies:</strong>{" "}
          {doctor.studies.join(", ")}
        </p>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">Languages:</strong>{" "}
          {doctor.languages.join(", ")}
        </p>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">Treatments:</strong>{" "}
          {getAssignedTreatments() || "None"}
        </p>
        <p className="text-xs text-slate-600 mb-1.5">
          <strong className="text-slate-800">
            Dentist Available for Booking:
          </strong>{" "}
          {doctor.IsBookDentist ? "Yes" : "No"}
        </p>
      </div>
      <div className="px-3 py-2.5 border-t border-slate-200 bg-slate-50 flex justify-end">
        <button
          className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
          onClick={onEdit}
        >
          Add Treatment
        </button>
      </div>
    </div>
  );
};

// Main Doctors Section
export const Doctors: React.FC<Props> = ({
  doctors,
  treatments,
  onDoctorsChange,
}) => {
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);

  // State for Add/Edit Doctor Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<IDoctor | null>(null);

  const handleOpenAddDoctor = () => {
    setEditingDoctor(null);
    setIsModalOpen(true);
  };

  const handleOpenEditDoctor = (doctor: IDoctor) => {
    setEditingDoctor(doctor);
    setIsModalOpen(true);
  };

  const handleSaveDoctor = (doctorData: IDoctor) => {
    if (editingDoctor) {
      // Edit Mode
      onDoctorsChange(
        doctors.map((d) =>
          d.id === editingDoctor.id ? { ...d, ...doctorData } : d
        )
      );
      console.log("Edited Doctor Data:", doctorData.assignedTreatmentIds);

      bookUrl
        .patch("/bookingclinic/clinic/addTreatmentEmployee", {
          treatmentids: doctorData.assignedTreatmentIds,
          employeeId: doctorData.id,
        })
        .then((res) => {
          if (res.data?.success) {
            setIsModalOpen(false);
            toast.success("Doctor updated successfully!");
          } else {
            toast.error("Failed to update doctor");
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error(
            error?.response?.data?.message || "Failed to update doctor"
          );
        });
    }
  };

  const handleDelete = () => {
    if (!deleteCandidate) return;
    onDoctorsChange(doctors.filter((d) => d.id !== deleteCandidate));
    setDeleteCandidate(null);
  };

  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pb-2.5 mb-3 border-b border-slate-200">
        <h2 className="text-xs font-bold text-primary mb-0">Doctors</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {doctors.map((doc, i) => (
          <DoctorCard
            key={i}
            doctor={doc}
            allTreatments={treatments}
            onDelete={() => setDeleteCandidate(doc.id)}
            onEdit={() => handleOpenEditDoctor(doc)} // Updated onEdit
          />
        ))}
      </div>

      {deleteCandidate && (
        <ConfirmationModal
          title="Delete Doctor"
          message={`Are you sure you want to delete Dr. ${
            doctors.find((d) => d.id === deleteCandidate)?.firstName
          }?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}

      {isModalOpen && (
        <DoctorFormModal
          doctor={editingDoctor}
          allTreatments={treatments}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveDoctor}
        />
      )}
    </section>
  );
};
